<template>
    <div>
        <div class="pg-head">
            <div class="pg-ico"><span class="mdi mdi-calendar-edit"></span></div>
            <div>
                <div class="id-h1">{{ $t('bookingMgmt.title') }}</div>
                <!-- subtitle now describes both modes ("ดูการจองตามวันที่" stopped being true when
                     รายเดือน shipped); same string as the Private page, already in all 5 languages -->
                <div class="pg-sub">{{ $t('privateBooking.viewSubtitle') }}</div>
            </div>
        </div>

        <!-- แท็บอีเวนต์/แข่งขันย้ายไปเป็นเมนู eventmanager ของตัวเองแล้ว (EVENT_SYSTEM_DESIGN.md §8 เฟส 1) -->
        <div>
        <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:12px;gap:10px;flex-wrap:wrap">
            <div class="isk-sec">{{ viewMode === 'month' ? $t('privateBooking.bookingsInMonth') : $t('bookingMgmt.allBookingsToday') }}</div>
            <div class="reserve-datebar" style="margin-bottom:0">
                <button class="pb-today" type="button" @click="goToday">{{ $t('privateBooking.today') }}</button>
                <span class="mdi mdi-calendar-check"></span> <b>{{ rangeLabel }}</b>
            </div>
        </div>

        <!-- month overview chip — quick totals for the whole displayed month -->
        <div v-if="viewMode === 'month' && BookingList.length" style="margin:-4px 0 12px">
            <span class="badge badge-primary">{{ $t('bookingMgmt.rangeSummary', { count: summary.count, students: summary.students, days: summary.days }) }}</span>
        </div>

        <div class="grid2 grid2-booking">
            <div class="scard" style="padding:16px;align-self:start;max-width:340px;width:100%">
                <div class="step-head"><span class="mdi mdi-calendar-search" style="color:var(--c-primary);font-size:22px"></span> {{ $t('privateBooking.viewByPeriod') }}</div>

                <!-- view-mode pills: รายวัน / รายเดือน. Unlike the Private page (≈10 bookings a month) the
                     group roster runs 1,000-1,300 rows a month, so the fetch is scoped to the mode —
                     รายวัน fetches ONE day (unchanged cost, incl. the 60s poll) and รายเดือน the month. -->
                <div class="pb-modes">
                    <button v-for="m in viewModes" :key="m.key" type="button"
                        class="pb-mode" :class="{ 'is-active': viewMode === m.key }" @click="setViewMode(m.key)">{{ m.label }}</button>
                </div>

                <!-- allow Monday + show holidays (mark-only, still pickable) — same as the Dashboard
                     calendar: this is a view filter, staff must be able to inspect any day's data.
                     Paging months (‹ ›) moves the anchor and refetches. -->
                <id-calendar
                    :model-value="calendarModel"
                    :mark-keys="markKeys"
                    :disable-mondays="false"
                    :holidays="holidayDetails"
                    @update:model-value="onPickDay"
                    @month-change="onMonthChange">
                </id-calendar>
                <div v-if="viewMode === 'month'" class="pb-legend"><span class="pb-dot"></span> {{ $t('privateBooking.markLegend') }}</div>
            </div>
            <div>
                <Transition name="fade" mode="out-in">
                    <div v-if="state == 'bookinglist'">
                        <id-data-grid
                            :columns="BookingListHeaders"
                            :rows="sortedBookingList"
                            :search-keys="['fullname', 'coursename', 'classtime', 'classdatedisplay']"
                            :filters="bookingFilters"
                            :search-placeholder="$t('btn.search')"
                            :loading="loadingBooking">
                            <template #actions>
                                <button class="id-btn id-btn-ghost id-btn-sm" @click="initialize">
                                    <span class="mdi mdi-refresh"></span> {{ $t('btn.refresh') }}
                                </button>
                                <button class="id-btn id-btn-primary id-btn-sm" @click="showAddNewBooking">
                                    <span class="mdi mdi-emoticon-plus-outline"></span> {{ $t('btn.newBooking') }}
                                </button>
                            </template>
                            <template #cell-view="{ row }">
                                <!-- เดิม mdi-eye-outline — เปลี่ยนเป็น icon ประวัติ 17 ส.ค. (dialog นี้แสดงประวัติการแก้ไขแล้ว) -->
                                <span class="mdi mdi-history" style="color:var(--c-primary);cursor:pointer;font-size:20px" @click="showBookingInfo(row)"></span>
                            </template>
                            <template #cell-checkin="{ row }">
                                <span v-if="parseInt(row.checkedin) === 0" class="mdi mdi-check-bold" style="color:var(--c-success);cursor:pointer;font-size:20px" @click="clickCheckin(row)"></span>
                                <span v-else-if="parseInt(row.checkedin) === 1" class="mdi mdi-close-thick" style="color:var(--c-error);cursor:pointer;font-size:20px" @click="clickUndoCheckin(row)"></span>
                            </template>
                            <template #cell-edit="{ row }">
                                <!-- เช็คชื่อแล้ว = ใบนิ่ง ห้ามย้าย/แก้ (เจ้าของเคาะ 18 ส.ค. — กติกาเดียวกับปุ่มยกเลิก
                                     ด้านล่าง) ต้อง undo เช็คชื่อก่อน · backend กันซ้ำที่ /updateBookingByAdmin -->
                                <span v-if="parseInt(row.checkedin) === 1" class="mdi mdi-lock-outline"
                                    style="color:var(--c-text-muted);font-size:20px" v-hint="$t('bookingMgmt.editNeedsUndo')"></span>
                                <span v-else class="mdi mdi-pencil" style="color:var(--c-info);cursor:pointer;font-size:20px" @click="clickEditBooking(row)"></span>
                            </template>
                            <template #cell-delete="{ row }">
                                <!-- เช็คชื่อแล้ว = ห้ามยกเลิกทุกบทบาท (ต้อง undo ก่อน) · เกิน 3 วัน = เฉพาะผู้จัดการ
                                     backend กันซ้ำทั้งสองเงื่อนไขที่ /cancelBookingByAdmin — ปุ่มแค่ไม่พาไปชนกำแพง -->
                                <span v-if="parseInt(row.checkedin) === 1" class="mdi mdi-lock-outline"
                                    style="color:var(--c-text-muted);font-size:20px" v-hint="$t('bookingMgmt.cancelNeedsUndo')"></span>
                                <span v-else-if="!canCancelRow(row)" class="mdi mdi-lock-outline"
                                    style="color:var(--c-text-muted);font-size:20px" v-hint="$t('bookingMgmt.cancelAskManager')"></span>
                                <span v-else class="mdi mdi-delete-forever" style="color:var(--c-error);cursor:pointer;font-size:20px" @click="deleteBookingItem(row)"></span>
                            </template>
                        </id-data-grid>
                        <!-- booking dialogs (modals — teleported, so layout/placement is unaffected) -->
                                            <id-modal v-model="dialogBookingEdit" size="lg" persistent
                                                :icon="editedBookingIndex == -1 ? 'mdi-emoticon-plus-outline' : 'mdi-human-edit'"
                                                :title="formBookingTitle">
                                                <v-form ref="bookingform">
                                                    <div v-if="editedBookingItem.courseinfo" class="t-cap" :class="courseinfoColor" style="margin-bottom:14px">{{ editedBookingItem.courseinfo }}</div>
                                                    <div class="form-grid">
                                                        <div class="field"><label>{{ $t('table.name') }} <span class="req">*</span></label>
                                                            <id-select v-model="editedBookingItem.studentid" searchable :disabled="editedBookingIndex != -1"
                                                                :placeholder="$t('common.selectStudent')" :search-placeholder="$t('bookingMgmt.searchStudentPlaceholder')"
                                                                :options="studentLookup.map(s => ({ value: s.studentid, label: s.name }))"
                                                                @update:model-value="onStudentChange"></id-select></div>
                                                        <div class="field"><label>{{ $t('table.courseName') }} <span class="req">*</span></label>
                                                            <id-select v-model="editedBookingItem.courseid" searchable :placeholder="$t('common.selectCourse')"
                                                                :options="courseLookup.map(c => ({ value: c.courseid, label: c.coursename }))"
                                                                @update:model-value="getClassTime"></id-select></div>
                                                        <div class="field"><label>{{ $t('table.date') }}</label>
                                                            <id-date v-model="selectedDate" :placeholder="$t('common.selectDate')" @update:model-value="getClassTime"></id-date></div>
                                                        <div class="field"><label>{{ $t('table.classTime') }} <span class="req">*</span></label>
                                                            <id-select :model-value="editedBookingItem.classtime ? editedBookingItem.classtime.classid : ''"
                                                                :placeholder="$t('common.selectClassTime')"
                                                                :options="classtimesData.map(c => ({ value: c.classid, label: c.disabled ? (c.classtime + ' — ' + $t('reservation.unavailable') + (c.description ? ' (' + c.description + ')' : '')) : c.text }))"
                                                                @update:model-value="id => editedBookingItem.classtime = classtimesData.find(c => c.classid === id)"></id-select></div>
                                                    </div>
                                                    <div class="id-cbx-row">
                                                    <label class="id-cbx" :style="{ opacity: editedBookingIndex != -1 ? .5 : 1 }"
                                                        @click="editedBookingIndex == -1 && (editedBookingItem.freeflag = editedBookingItem.freeflag ? 0 : 1)">
                                                        <span class="id-check" :class="{ on: editedBookingItem.freeflag == 1 }"><span class="mdi mdi-check"></span></span>
                                                        {{ $t('bookingMgmt.freeClass') }}
                                                    </label>
                                                    <label class="id-cbx"
                                                        @click="editedBookingItem.blindflag = editedBookingItem.blindflag ? 0 : 1">
                                                        <span class="id-check" :class="{ on: editedBookingItem.blindflag == 1 }"><span class="mdi mdi-check"></span></span>
                                                        {{ $t('bookingMgmt.blindBooking') }}
                                                    </label>
                                                    </div>
                                                    <!-- เรียนรายครั้ง (walk-in) — เฉพาะการจองใหม่ และไม่ใช่ทดลองเรียนฟรี
                                                         ระบบจะสร้างแพ็กเกจ 1 ครั้งให้อัตโนมัติ ไม่แตะคอร์สหลักของเด็ก
                                                         ส่วนชำระเงินใช้ layout เดียวกับ dialog สร้างคอร์ส (CustomerCourse.vue)
                                                         ทุกแถว — เจ้าของขอให้เหมือนกันเพื่อความคุ้นเคย (2026-08-04) -->
                                                    <template v-if="editedBookingIndex == -1 && editedBookingItem.freeflag != 1">
                                                        <label class="id-cbx" style="margin-top:12px;display:flex;width:fit-content" @click="togglePerSession">
                                                            <span class="id-check" :class="{ on: perSessionMode }"><span class="mdi mdi-check"></span></span>
                                                            {{ $t('bookingMgmt.perSessionToggle') }}
                                                        </label>
                                                        <template v-if="perSessionMode">
                                                            <div class="modal-sec mt"><span class="mdi mdi-cash-multiple"></span> {{ $t('table.payment') }}</div>
                                                            <div v-if="hasMainCourse" class="t-cap" style="color:var(--c-warning);margin-top:4px">{{ $t('bookingMgmt.perSessionHasMainCourse') }}</div>
                                                            <!-- row 1: จ่ายแล้ว/ยังไม่จ่าย (label สลับตามสถานะ — แบบเดียวกับหน้าคอร์ส) -->
                                                            <div class="form-grid-3" style="align-items:center;margin-top:4px">
                                                                <label class="id-cbx" @click="togglePsPaid">
                                                                    <span class="id-check" :class="{ on: psPaid == 1 }"><span class="mdi mdi-check"></span></span>
                                                                    {{ psPaid == 1 ? $t('customerCourse.paid') : $t('customerCourse.unpaid') }}
                                                                </label>
                                                            </div>
                                                            <!-- row 2: ราคา + วันที่ชำระ + วิธีชำระ -->
                                                            <div class="form-grid-3" style="align-items:end;margin-top:14px">
                                                                <div class="field"><label>{{ $t('table.price') }} <span class="req">*</span></label>
                                                                    <id-money v-model="psPrice" placeholder="0.00" @update:model-value="onPsPriceChanged"></id-money></div>
                                                                <div class="field"><label>{{ $t('customerCourse.payDate') }} <span v-if="psPaid == 1" class="req">*</span></label>
                                                                    <id-date v-model="psPaydate" :placeholder="$t('common.selectDate')" :disabled="psPaid != 1"></id-date></div>
                                                                <div class="field"><label>{{ $t('customerCourse.paymentMethod') }} <span v-if="psPaid == 1" class="req">*</span></label>
                                                                    <id-select v-model="psPaymentMethod" :placeholder="$t('customerCourse.selectPayment')" :options="psPaymentOptions" :disabled="psPaid != 1"
                                                                        @update:model-value="onPsMethodChanged"></id-select></div>
                                                            </div>
                                                            <!-- row 3: ค่าธรรมเนียม readonly + ปุ่มดินสอขอแก้เอง + hint (pattern เดียวกับหน้าคอร์ส) -->
                                                            <div class="form-grid-3" style="align-items:end;margin-top:14px">
                                                                <div class="field">
                                                                    <label>{{ $t('customerCourse.paymentFee') }} <span v-if="psPaid == 1" class="req">*</span></label>
                                                                    <div class="fee-wrap">
                                                                        <id-money v-model="psFeeValue" placeholder="0.00" :readonly="!psFeeManual"
                                                                            :class="{ 'fee-readonly': !psFeeManual }"></id-money>
                                                                        <button v-if="!psFeeManual" type="button" class="fee-btn" v-hint="$t('customerCourse.paymentFeeEditBtn')"
                                                                            :aria-label="$t('customerCourse.paymentFeeEditBtn')" @click="showPsFeeEditConfirm = true">
                                                                            <span class="mdi mdi-pencil-outline"></span>
                                                                        </button>
                                                                        <button v-else type="button" class="fee-btn" v-hint="$t('customerCourse.paymentFeeResetBtn')"
                                                                            :aria-label="$t('customerCourse.paymentFeeResetBtn')" @click="resetPsFeeToAuto">
                                                                            <span class="mdi mdi-restore"></span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div class="field" style="grid-column:span 2">
                                                                    <div class="t-cap" style="line-height:1.6">
                                                                        <span v-if="psFeeManual" style="color:var(--c-warning)">
                                                                            <span class="mdi mdi-pencil"></span>
                                                                            {{ $t('customerCourse.paymentFeeManualHint', { amount: formatMoney(psComputedFee) }) }}
                                                                        </span>
                                                                        <span v-else-if="psPaymentMethod === 'credit'">
                                                                            <span class="mdi mdi-information-outline"></span>
                                                                            {{ $t('customerCourse.paymentFeeAutoHint', { pct: psFeeRatePercentLabel }) }}
                                                                        </span>
                                                                        <span v-else>
                                                                            <span class="mdi mdi-information-outline"></span>
                                                                            {{ $t('customerCourse.paymentFeeNoneHint') }}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <!-- row 4: สลิป — multi-slip โหมด "รอแนบตอนบันทึก" (จอนี้สร้างใบใหม่เสมอ
                                                                 ยังไม่มี courserefer) ไฟล์ทั้งหมดไปกับ FormData ตอนกดจอง
                                                                 รายการสลิปจริง + ปุ่มลบไปดูได้ที่หน้าคอร์สของลูกค้าหลังจอง -->
                                                            <div class="field full" style="margin-top:14px">
                                                                <slip-list-panel :pending-files="psSlipFiles" :disabled="psPaid != 1"
                                                                    @update:pending-files="v => psSlipFiles = v"
                                                                    @error="msg => $emit('onErrorHandler', msg)"></slip-list-panel>
                                                            </div>
                                                            <!-- หมายเหตุ (บันทึกลง shortnote ของแพ็กเกจรายครั้ง) -->
                                                            <div class="field full" style="margin-top:14px"><label>{{ $t('customerCourse.shortNote') }}</label>
                                                                <textarea class="id-input id-textarea" v-model="psShortnote" maxlength="500"></textarea>
                                                                <div class="t-cap" style="text-align:right;margin-top:4px">{{ (psShortnote || '').length }}/500</div></div>
                                                        </template>
                                                    </template>
                                                </v-form>
                                                <!-- audit info — only when editing an existing booking (same data as the 👁️ dialog) -->
                                                <template v-if="editedBookingIndex != -1">
                                                    <div class="modal-sec mt"><span class="mdi mdi-information-outline"></span> {{ $t('bookingMgmt.bookingInfoTitle') }}</div>
                                                    <div class="info-rows">
                                                        <div class="info-row">
                                                            <span class="info-label"><span class="mdi mdi-shield-account-outline"></span> {{ $t('bookingMgmt.visibility') }}</span>
                                                            <span class="info-val">
                                                                <span class="badge" :class="parseInt(editedBookingItem.blindflag) === 1 ? 'badge-private' : 'badge-public'">
                                                                    <span class="mdi" :class="parseInt(editedBookingItem.blindflag) === 1 ? 'mdi-eye-off' : 'mdi-eye'"></span>
                                                                    {{ parseInt(editedBookingItem.blindflag) === 1 ? $t('bookingMgmt.visPrivate') : $t('bookingMgmt.visPublic') }}
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div class="info-row">
                                                            <span class="info-label"><span class="mdi mdi-account-plus-outline"></span> {{ $t('bookingMgmt.createBy') }}</span>
                                                            <span class="info-val">{{ editedBookingItem.createby || '-' }}</span>
                                                        </div>
                                                        <div class="info-row">
                                                            <span class="info-label"><span class="mdi mdi-clock-plus-outline"></span> {{ $t('bookingMgmt.createDate') }}</span>
                                                            <span class="info-val">{{ format_datetime(editedBookingItem.createdate) || '-' }}</span>
                                                        </div>
                                                    </div>
                                                </template>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="closeEditBooking">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary"
                                                        :disabled="!editedBookingItem.studentid || !editedBookingItem.courseid || !editedBookingItem.classtime || psInvalid"
                                                        @click="doSaveNewBooking">
                                                        <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
                                                </template>
                                            </id-modal>
                                            <id-modal v-model="dialogBookingDelete" size="sm" icon="mdi-delete-alert-outline" :title="$t('common.confirmDeleteTitle')" persistent>
                                                <p style="margin:0">{{ $t('bookingMgmt.confirmDelete', { name: editedBookingItem.fullname }) }}</p>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="clickCancelDeleteBooking">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="clickConfirmDeleteBooking"><span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
                                                </template>
                                            </id-modal>
                                            <!-- confirm ชั้นที่สอง: ใบเรียนรายครั้งที่จ่ายเงินแล้ว — การยกเลิกจะลบใบซื้อออกจากระบบด้วย -->
                                            <id-modal v-model="dialogPsPaidCancel" size="sm" icon="mdi-cash-refund" :title="$t('bookingMgmt.perSessionPaidCancelTitle')" persistent>
                                                <p style="margin:0">{{ $t('bookingMgmt.perSessionPaidCancelBody', { refer: editedBookingItem.courserefer }) }}</p>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="dialogPsPaidCancel = false">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmPsPaidCancel"><span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
                                                </template>
                                            </id-modal>
                                            <!-- ยืนยันก่อนปลดล็อกช่องค่าธรรมเนียม — pattern เดียวกับ dialog สร้างคอร์ส -->
                                            <id-modal v-model="showPsFeeEditConfirm" size="sm" icon="mdi-pencil-outline" :title="$t('customerCourse.paymentFeeConfirmTitle')">
                                                <p style="margin:0">{{ $t('customerCourse.paymentFeeConfirmBody', { amount: formatMoney(psComputedFee) }) }}</p>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="showPsFeeEditConfirm = false">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary" @click="confirmPsFeeEdit">
                                                        <span class="mdi mdi-pencil"></span> {{ $t('customerCourse.paymentFeeConfirmOk') }}</button>
                                                </template>
                                            </id-modal>
                                            <id-modal v-model="dialogCheckin" size="sm" icon="mdi-check-circle-outline" :title="$t('bookingList.menuCheckin')" persistent>
                                                <p style="margin:0">{{ $t('bookingMgmt.confirmCheckin', { name: editedBookingItem.fullname }) }}</p>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="clickCancelCheckinDialog">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary" @click="clickConfirmCheckinDialog"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
                                                </template>
                                            </id-modal>
                                            <id-modal v-model="dialogUndoCheckin" size="sm" icon="mdi-undo-variant" :title="$t('bookingList.menuUndoCheckin')" persistent>
                                                <p style="margin:0">{{ $t('bookingMgmt.confirmUndoCheckin', { name: editedBookingItem.fullname }) }}</p>
                                                <template #footer>
                                                    <button class="id-btn id-btn-ghost" @click="clickCancelUndoCheckinDialog">{{ $t('btn.cancel') }}</button>
                                                    <button class="id-btn id-btn-primary" @click="clickConfirmUndoCheckinDialog"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
                                                </template>
                                            </id-modal>
                                            <!-- booking audit info — opens on row double-click -->
                                            <id-modal v-model="dialogBookingInfo" size="sm" icon="mdi-information-outline"
                                                :title="$t('bookingMgmt.bookingInfoTitle')" :subtitle="bookingInfoItem.fullname">
                                                <div class="info-rows">
                                                    <div class="info-row">
                                                        <span class="info-label"><span class="mdi mdi-shield-account-outline"></span> {{ $t('bookingMgmt.visibility') }}</span>
                                                        <span class="info-val">
                                                            <span class="badge" :class="parseInt(bookingInfoItem.blindflag) === 1 ? 'badge-private' : 'badge-public'">
                                                                <span class="mdi" :class="parseInt(bookingInfoItem.blindflag) === 1 ? 'mdi-eye-off' : 'mdi-eye'"></span>
                                                                {{ parseInt(bookingInfoItem.blindflag) === 1 ? $t('bookingMgmt.visPrivate') : $t('bookingMgmt.visPublic') }}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    <div class="info-row">
                                                        <span class="info-label"><span class="mdi mdi-account-plus-outline"></span> {{ $t('bookingMgmt.createBy') }}</span>
                                                        <span class="info-val">{{ bookingInfoItem.createby || '-' }}</span>
                                                    </div>
                                                    <div class="info-row">
                                                        <span class="info-label"><span class="mdi mdi-clock-plus-outline"></span> {{ $t('bookingMgmt.createDate') }}</span>
                                                        <span class="info-val">{{ format_datetime(bookingInfoItem.createdate) || '-' }}</span>
                                                    </div>
                                                    <template v-if="bookingInfoItem.updateby">
                                                        <div class="info-row">
                                                            <span class="info-label"><span class="mdi mdi-account-edit-outline"></span> {{ $t('bookingMgmt.updateBy') }}</span>
                                                            <span class="info-val">{{ bookingInfoItem.updateby }}</span>
                                                        </div>
                                                        <div class="info-row">
                                                            <span class="info-label"><span class="mdi mdi-clock-edit-outline"></span> {{ $t('bookingMgmt.editDate') }}</span>
                                                            <span class="info-val">{{ format_datetime(bookingInfoItem.editdate) || '-' }}</span>
                                                        </div>
                                                    </template>
                                                </div>
                                                <edit-history-timeline v-if="dialogBookingInfo" kind="booking" :refid="bookingInfoItem.reservationid"
                                                    :preloaded="historyPreload"></edit-history-timeline>
                                                <template #footer>
                                                    <button class="id-btn id-btn-primary" @click="dialogBookingInfo = false">{{ $t('btn.close') }}</button>
                                                </template>
                                            </id-modal>
                    </div>
                </Transition>
            </div>
        </div>
        </div>
    <id-modal v-model="errorDialog" size="sm" icon="mdi-alert-circle-outline" :title="$t('dialog.error')">
        <p style="margin:0">{{ errorMsg }}</p>
        <template #footer>
            <button class="id-btn id-btn-primary" @click="errorDialog = false">{{ $t('btn.close') }}</button>
        </template>
    </id-modal>

    <id-modal v-model="infoDialog" size="sm" icon="mdi-check-circle-outline" :title="$t('dialog.success')">
        <p style="margin:0">{{ infoMsg }}</p>
        <template #footer>
            <button class="id-btn id-btn-primary" @click="infoDialog = false">{{ $t('btn.ok') }}</button>
        </template>
    </id-modal>
    </div>
</template>
<script>
import axios from 'axios'
import live from '@/utils/live'
import moment from 'moment'
import { mapGetters } from 'vuex';
import { formatDateTimeBKK } from '../../utils/datetime';
import { formatMonthYear, toDate } from '../../utils/locale';
import { formatMoney, normalizeMoneyInput } from '../../utils/money';
import { fetchEditHistory } from '@/utils/history';
export default ({
    data() {
        return {
            search: '',
            errorDialog: false,
            errorMsg: '',
            infoDialog: false,
            infoMsg: '',
            interval: null,
            date: new Date(),
            // 'day' = grid shows the selected day | 'month' = grid shows the whole displayed month.
            // Unlike PrivateBookingManagement (which always fetches the month and filters client-side),
            // switching mode here REFETCHES: a group-class month is ~1,000-1,300 rows, so the daily
            // check-in view must not pay for data it never shows — nor poll it every 60s.
            viewMode: 'day',
            classday: '',
            pulse: '',
            classtimesData: [],
            loadingCourse: false,
            loadingClassTime: false,
            BookingList: [],
            BookingListHeadersData: [],
            editedBookingItem: {
                fullname: null,
                reservationid: null,
                studentid: null,
                courseid: null,
                classid: null,
                classdate: null,
                classtime: null,
                courseinfo: null,
                courserefer: null,
                freeflag: 0,
                blindflag: 0,
            },
            defaultBookingItem: {
                fullname: null,
                reservationid: null,
                studentid: null,
                courseid: null,
                classid: null,
                classdate: null,
                classtime: null,
                courseinfo: null,
                courserefer: null,
                freeflag: 0,
                blindflag: 0,
            },
            courseinfoColor: 'courseinfoColorGreen',
            // ── เรียนรายครั้ง (walk-in) ──
            perSessionMode: false,
            psPrice: null,
            psPaymentMethod: '',
            psPaid: 1,
            psPaydate: null,
            psSlipFiles: [],   // multi-slip: คิวไฟล์สลิปของใบจองรายครั้งที่กำลังสร้าง
            psShortnote: '',
            // ค่าธรรมเนียม: readonly + คำนวณอัตโนมัติโดยปริยาย แอดมินกดดินสอขอแก้เองได้
            // (pattern เดียวกับ CustomerCourse.vue เป๊ะ — เจ้าของขอให้สองฟอร์มเหมือนกัน)
            psFeeValue: '0.00',
            psFeeManual: false,
            showPsFeeEditConfirm: false,
            coursePrices: [],
            creditCardFeeRate: 0.03, // ค่าจริงอ่านจาก /getCoursePrices (single source of truth ฝั่ง server)
            dialogPsPaidCancel: false,
            selectedDate: null,
            editedBookingIndex: -1,
            dialogBookingEdit: false,
            dialogCheckin: false,
            dialogBookingDelete: false,
            loadingBooking: false,
            dialogUndoCheckin: false,
            dialogBookingInfo: false,
            bookingInfoItem: {},
            historyPreload: null,   // ไทม์ไลน์การแก้ไขที่โหลดไว้ก่อนเปิด dialog ข้อมูลใบจอง
            state: 'bookinglist',
            // [{ holidaydate, description }] from /holidaysList — marks holiday days on the calendar
            holidayDetails: [],

            notNullRules: [v => !!v || this.$t('common.required'),]
        }
    },

    async created() {
        //console.log('BookingManagement created...' + new Date())
        this.$emit('onLoading', true)
        await this.initialize()
        this.$emit('onLoading', false)
    },
    mounted() {
        //console.log('mounted...' + new Date())
        // ⚠️ โพล 60 วิยังอยู่ **ห้ามเอาออก** — สายเรียลไทม์เป็นของแถม ไม่ใช่ของหลัก
        // (ถ้า DO ขยายเป็นหลาย instance เครื่องที่ถือสายจะไม่ใช่เครื่องที่รับ write เสมอไป)
        this.interval = setInterval(() => {
            this.refreshData(true) // silent background poll — no loading flicker, no error dialogs
        }, 60000)
        // สัญญาณบอกแค่ว่า "ข้อมูลเปลี่ยนแล้ว" — จอดึงเองด้วย API เดิมที่ผ่านสิทธิ์ครบทุกชั้น
        this.offLive = live.subscribe(['booking', 'event'], () => this.refreshData(true))
    },
    unmounted() {
        //console.log('unmounted...' + new Date())
        clearInterval(this.interval)
        if (this.offLive) this.offLive()
    },
    methods: {
        async initialize() {
            await this.getReservationList()
            await this.getCourseLookup()
            await this.getFamilyLookup()
            await this.getStudentLookup()
            this.fetchHolidays()
        },
        async fetchHolidays() {
            try {
                const token = this.$store.getters.getToken;
                const res = await axios.get(this.baseURL + '/holidaysList', { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.success) this.holidayDetails = res.data.details || [];
            } catch (e) { /* holidays are non-critical for the booking calendar */ }
        },
        async refreshData(silent = false) {
            //console.log('refreshData...' + new Date())
            await this.getReservationList(silent)
        },
        // ── period view (ดูตามช่วงเวลา) — mirrors PrivateBookingManagement.vue, except the fetch is
        //    scoped to the mode instead of always pulling the whole month (see `viewMode` in data()).
        async setViewMode(mode) {
            if (this.viewMode === mode) return
            this.viewMode = mode
            await this.getReservationList()
        },
        // tapped a day on the calendar — select it and reload. In month mode the fetched span only
        // changes when the month does, so staying inside the month needs no refetch.
        async onPickDay(key) {
            const p = key.split('-')
            const d = new Date(+p[0], +p[1] - 1, +p[2])
            const monthChanged = d.getFullYear() !== this.date.getFullYear() || d.getMonth() !== this.date.getMonth()
            this.date = d
            this.state = 'bookinglist'
            if (this.viewMode === 'day' || monthChanged) await this.getReservationList()
        },
        // paged the calendar to another month (‹ › / month picker) — move the anchor there (clamping the
        // day so e.g. Jan 31 → Feb 28) and refetch
        async onMonthChange({ year, month }) {
            const dim = moment([year, month]).daysInMonth()
            this.date = new Date(year, month, Math.min(this.date.getDate(), dim))
            await this.getReservationList()
        },
        async goToday() {
            this.date = new Date()
            this.state = 'bookinglist'
            await this.getReservationList()
        },
        // the span the grid currently shows — day mode is a single day, month mode the whole month.
        // `sig` is used to drop responses that land after the user moved on.
        currentRange() {
            if (this.viewMode === 'month') {
                return { startdate: this.SQLDate(this.monthStart), enddate: this.SQLDate(this.monthEnd) }
            }
            const day = this.SQLDate(this.date)
            return { startdate: day, enddate: day }
        },
        async onStudentChange() {
            //console.log('Student selected:', studentid);
            this.loadingCourse = true
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/getCustomerCourseInfo', {
                studentid: this.editedBookingItem.studentid,
            },
                {
                    headers: { Authorization: `Bearer ${token}`, }
                })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        //console.log('getCustomerCourseInfo', response.data.results);
                        const res = response.data.results[0];
                        if (res) {
                            this.courseinfoColor = 'courseinfoColorGreen'
                            this.editedBookingItem.courseid = res.courseid
                            if (res.coursetype == 'Monthly') {
                                this.editedBookingItem.courseinfo = this.$t('bookingMgmt.courseInfoMonthly', { ref: res.courserefer, expiry: this.format_date(res.expiredate) })
                            } else {
                                this.editedBookingItem.courseinfo = this.$t('bookingMgmt.courseInfoLimited', { ref: res.courserefer, expiry: this.format_date(res.expiredate), remaining: res.remaining })
                            }
                        } else {
                            this.courseinfoColor = 'courseinfoColorRed'
                            this.editedBookingItem.courseinfo = this.$t('bookingMgmt.noCourse')
                        }
                    } else {
                        this.$emit('onErrorHandler', 'getCustomerCourseInfo failed');
                    }
                })
                .catch(error => {
                    //console.log(error)
                    if (error.response && error.response.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
            this.loadingCourse = false
        },
        async getClassTime() {
            if (this.editedBookingItem.courseid == null) return;
            if (this.selectedDate == null) return;
            this.loadingClassTime = true
            this.classtimesData = []
            let req = {
                classdate: this.SQLDate(this.selectedDate),
                classday: toDate(this.selectedDate).toLocaleDateString('en-US', { weekday: 'long' }),
                courseid: this.editedBookingItem.courseid
            }
            //console.log("request", req)
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/getClassTime', req, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        const data = response.data.results
                        if (data.length == 0) {
                            this.classtimesData = []
                            this.editedBookingItem.classtime = null
                        } else {
                            this.classtimesData = data;
                            if (this.editedBookingItem.classtime != null) {
                                if (typeof this.editedBookingItem.classtime === 'object' && this.editedBookingItem.classtime !== null) {
                                    this.editedBookingItem.classtime = this.classtimesData.find(x => x.classtime == this.editedBookingItem.classtime.classtime)
                                } else {
                                    this.editedBookingItem.classtime = this.classtimesData.find(x => x.classtime == this.editedBookingItem.classtime)
                                }
                            }
                        }
                    } else {
                        this.classtimesData = []
                        this.editedBookingItem.classtime = null
                    }
                    this.loadingClassTime = false
                })

        },
        async getCourseLookup() {
            const token = this.$store.getters.getToken;
            await axios
                .get(this.baseURL + '/courseLookup', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        this.courseLookup = response.data.results
                    }
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        async getFamilyLookup() {
            const token = this.$store.getters.getToken;
            await axios
                .get(this.baseURL + '/familyLookup', { headers: { Authorization: `Bearer ${token}`, } })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        this.familyLookup = response.data.results
                    }
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        async getStudentLookup() {
            const token = this.$store.getters.getToken;
            await axios
                .post(this.baseURL + '/studentLookup', {}, { headers: { Authorization: `Bearer ${token}`, } },)
                .then(response => {
                    //console.dir('studentLookup', response);
                    if (response.data.success) {
                        this.studentLookup = response.data.results
                    }
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        async doSaveNewBooking() {
            this.$emit('onLoading', true)
            const { valid } = await this.$refs.bookingform.validate()
            
            if (valid) {
                this.$emit('onLoading', true)
                // Make API request to register the user
                const BookingObj = {
                    studentid: this.editedBookingItem.studentid,
                    courseid: this.editedBookingItem.courseid,
                    classid: this.editedBookingItem.classtime.classid,
                    classdate: this.SQLDate(this.selectedDate),
                    classtime: this.editedBookingItem.classtime.classtime,
                    classday: this.editedBookingItem.classtime.classday,
                    reservationid: this.editedBookingItem.reservationid,
                    freeflag: this.editedBookingItem.freeflag,
                    blindflag: this.editedBookingItem.blindflag ? 1 : 0,
                }
                //console.log(this.editedBookingIndex + ' BookingObj : ', BookingObj)

                const token = this.$store.getters.getToken;
                if (this.editedBookingIndex > -1) {
                    BookingObj.reservationid = this.editedBookingItem.reservationid
                    await axios
                        .post(this.baseURL + '/updateBookingByAdmin', BookingObj, { headers: { Authorization: `Bearer ${token}`, } })
                        .then(response => {
                            if (response.data.success) {
                                this.$emit('onInfoHandler', this.$t('msg.updated'));
                                this.getReservationList()
                                this.dialogBookingEdit = false
                            } else {
                                this.$emit('onErrorHandler', response.data.message || this.$t('msg.updateFail'));
                            }
                        })
                        .catch(error => {
                            if (error.response?.status == 401) {
                                this.$emit('onErrorHandler', error.response.data.message)
                                this.$emit('onClickChangeState', 'login')
                            } else {
                                this.$emit('onErrorHandler', error.message)
                            }
                        });
                } else if (this.perSessionMode && this.editedBookingItem.freeflag != 1) {
                    // เรียนรายครั้ง — endpoint แยก: server สร้างแพ็กเกจ 1 ครั้ง + ใบจอง ในธุรกรรมเดียว
                    await this.doSavePerSessionBooking()
                } else {
                    await axios
                        .post(this.baseURL + '/addBookingByAdmin', BookingObj, { headers: { Authorization: `Bearer ${token}`, } })
                        .then(response => {
                            if (response.data.success) {
                                this.$emit('onInfoHandler', this.$t('msg.bookingAdded'));
                                this.getReservationList()
                                this.dialogBookingEdit = false
                            } else {
                                this.$emit('onErrorHandler', response.data.message || this.$t('msg.bookingAddFail'));
                            }
                            this.$emit('onUpdateDataSuccess')
                        })
                        .catch(error => {
                            if (error.response?.status == 401) {
                                this.$emit('onErrorHandler', error.response.data.message)
                                this.$emit('onClickChangeState', 'login')
                            } else {
                                this.$emit('onErrorHandler', error.message)
                            }
                        });
                }
                this.$emit('onLoading', false)
            } else {
                this.$emit('onErrorHandler', this.$t('msg.required'))
                this.$emit('onLoading', false)
                return
            }

        },
        deleteBookingItem(item) {
            this.editedBookingIndex = this.BookingList.indexOf(item)
            this.editedBookingItem = Object.assign({}, item)
            this.dialogBookingDelete = true
        },
        // เกิน 3 วันหลังวันเรียน = บทบาทอื่นนอกจากผู้จัดการยกเลิกไม่ได้ (เจ้าของเคาะ 2026-08-10)
        // ไม่รู้วัน → ปล่อยผ่านให้ backend เป็นคนตัดสิน (มันบังคับกติกาเดียวกันเสมอ)
        canCancelRow(row) {
            if (this.isHead) return true;
            const d = row.classdatekey || row.classdate;
            if (!d) return true;
            return moment(d).isSameOrAfter(moment().subtract(3, 'days'), 'day');
        },
        async clickConfirmUndoCheckinDialog() {
            try {
                // ยิงไปหลังบ้านเพื่ออัพเดทข้อมูล checkin
                await this.updateUndoCheckinStatus();

                // บันทึกตำแหน่งการ scroll ของหน้าจอ
                const scrollTop = window.scrollY;

                // โหลดข้อมูลใหม่
                await this.getReservationList();

                // คืนค่าตำแหน่งการ scroll ของหน้าจอ
                this.$nextTick(() => {
                window.scrollTo(0, scrollTop);
                });
            } catch (error) {
                console.error('Error in clickConfirmUndoCheckinDialog:', error);
            }
            },
            async clickConfirmCheckinDialog() {
            try {
                // ยิงไปหลังบ้านเพื่ออัพเดทข้อมูล checkin
                await this.updateCheckinStatus();

                // บันทึกตำแหน่งการ scroll ของหน้าจอ
                const scrollTop = window.scrollY;

                // โหลดข้อมูลใหม่
                await this.getReservationList();

                // คืนค่าตำแหน่งการ scroll ของหน้าจอ
                this.$nextTick(() => {
                window.scrollTo(0, scrollTop);
                });
            } catch (error) {
                console.error('Error in clickConfirmCheckinDialog:', error);
            }
        },
        async updateCheckinStatus() {
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/checkinByAdmin', {
                reservationid: this.editedBookingItem.reservationid,
                studentid: this.editedBookingItem.studentid,
            },
                {
                    headers: { Authorization: `Bearer ${token}`, }
                })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        //this.$emit('onInfoHandler', 'Check-in Successful');
                    } else {
                        this.$emit('onErrorHandler', response.data.message || this.$t('bookingList.checkinFail'));
                    }
                    this.dialogCheckin = false
                })
                .catch(error => {
                    console.log(error)
                    if (error.response?.status && error.response.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        async updateUndoCheckinStatus() {
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/undoCheckinByAdmin', {
                reservationid: this.editedBookingItem.reservationid,
                studentid: this.editedBookingItem.studentid,
            },
                {
                    headers: { Authorization: `Bearer ${token}`, }
                })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        //this.$emit('onInfoHandler', 'Cancel Check-in Successful');
                    } else {
                        this.$emit('onErrorHandler', response.data.message || this.$t('bookingList.undoCheckinFail'));
                    }
                    this.dialogUndoCheckin = false
                })
                .catch(error => {
                    console.log(error)

                    if (error.response?.status && error.response.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        async clickConfirmDeleteBooking() {
            // ใบเรียนรายครั้งที่จ่ายเงินแล้ว → ต้องยืนยันชั้นที่สองก่อน (การยกเลิกจะลบใบซื้อลงประวัติด้วย)
            // ไม่ผ่านชั้นที่สอง = ไม่มีอะไรถูกส่งไป server เลย (การจองและใบซื้อคงเดิมทั้งคู่)
            if (Number(this.editedBookingItem.per_session) === 1 && Number(this.editedBookingItem.pkg_paid) === 1) {
                this.dialogBookingDelete = false
                this.dialogPsPaidCancel = true
                return
            }
            await this.doCancelBookingRequest()
        },
        async confirmPsPaidCancel() {
            this.dialogPsPaidCancel = false
            await this.doCancelBookingRequest()
        },
        async doCancelBookingRequest() {
            this.$emit('onLoading', true)
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/cancelBookingByAdmin', {
                reservationid: this.editedBookingItem.reservationid,
                studentid: this.editedBookingItem.studentid,
                courserefer: this.editedBookingItem.courserefer,
            },
                {
                    headers: { Authorization: `Bearer ${token}`, }
                })
                .then(response => {
                    //console.dir(response);
                    if (response.data.success) {
                        this.$emit('onInfoHandler', response.data.message || this.$t('msg.reservationDeleted'));
                    } else {
                        this.$emit('onErrorHandler', response.data.message || this.$t('msg.reservationDeleteFail'));
                    }
                    this.dialogBookingDelete = false
                    this.getReservationList()
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
            this.$emit('onLoading', false)
        },
        // เตรียมข้อมูลให้ครบก่อนเปิด dialog (กติกาเดียวกับ showAddNewBooking ที่ทำอยู่แล้ว)
        // — โดยเฉพาะ getClassTime ที่เดิมปล่อยลอย ทำให้ตัวเลือก "เวลาเรียน" โผล่ทีหลัง
        async clickEditBooking(item) {
            this.$emit('onLoading', true)
            try {
                await this.getCourseLookup()
                await this.getFamilyLookup()
                await this.getStudentLookup()
                this.courseinfoColor = 'courseinfoColorGreen'
                this.editedBookingIndex = this.BookingList.indexOf(item)
                this.editedBookingItem = Object.assign({}, item)
                this.selectedDate = new Date(this.editedBookingItem.classdate)
                await this.getClassTime()
            } finally {
                this.$emit('onLoading', false)
            }
            this.dialogBookingEdit = true
        },
        clickCheckin(item) {
            //console.log('clickCheckin', item)
            this.editedBookingIndex = this.BookingList.indexOf(item)
            this.editedBookingItem = Object.assign({}, item)
            this.clickConfirmCheckinDialog()
        },
        clickUndoCheckin(item) {
            //console.log('clickUndoCheckin', item)
            this.editedBookingIndex = this.BookingList.indexOf(item)
            this.editedBookingItem = Object.assign({}, item)
            this.clickConfirmUndoCheckinDialog()
        },
        clickCancelCheckinDialog() {
            this.dialogCheckin = false
            setTimeout(() => {
                this.$nextTick(() => {
                    this.editedBookingItem = Object.assign({}, this.defaultBookingItem)
                    this.editedBookingIndex = -1
                })
            }, 300)
        },
        clickCancelUndoCheckinDialog() {
            this.dialogUndoCheckin = false
            setTimeout(() => {
                this.$nextTick(() => {
                    this.editedBookingItem = Object.assign({}, this.defaultBookingItem)
                    this.editedBookingIndex = -1
                })
            }, 300)
        },
        closeEditBooking() {
            this.dialogBookingEdit = false
            setTimeout(() => {
                this.$nextTick(() => {
                    this.editedBookingItem = Object.assign({}, this.defaultBookingItem)
                    this.selectedDate = null
                    this.editedBookingIndex = -1
                    this.classtimesData = []
                    this.resetPsFields()
                })
            }, 300)
        },
        clickCancelDeleteBooking() {
            this.dialogBookingDelete = false
            setTimeout(() => {
                this.$nextTick(() => {
                    // กำลังรอยืนยันชั้นที่สอง (ใบรายครั้งจ่ายแล้ว) — ห้ามล้างใบจองที่เลือกไว้
                    if (this.dialogPsPaidCancel) return
                    this.editedBookingItem = Object.assign({}, this.defaultBookingItem)
                    this.editedBookingIndex = -1
                })
            }, 300)
        },
        addOneDay(date = new Date()) {
            date.setDate(date.getDate() + 1);
            return date;
        },
        // silent=true for the 60s background poll — update the table in place (no skeleton/$minLoad)
        // and swallow transient errors (only 401 still forces re-login)
        async getReservationList(silent = false) {
            const range = this.currentRange()
            const sig = range.startdate + '|' + range.enddate
            if (!silent) this.loadingBooking = true
            const t0 = Date.now()
            const token = this.$store.getters.getToken;
            await DashboardAPI.fetchDataBooking({ token, ...range })
                .then(async ({ success, results, message }) => {
                    // the user picked another date/mode while this request was in flight — drop the stale response
                    const now = this.currentRange()
                    const stale = sig !== now.startdate + '|' + now.enddate
                    if (silent && stale) return
                    if (success) {
                        // stamp the display date onto the fetched rows themselves (not a mapped copy in
                        // the computed) so row identity survives for BookingList.indexOf(item)
                        results.forEach(r => { r.classdatedisplay = this.format_date(r.classdatekey || r.classdate) })
                        this.BookingList = results
                        if (!silent && !stale) {
                            await this.$minLoad(t0)
                            this.loadingBooking = false
                        }
                    } else if (!silent) {
                        await this.$minLoad(t0)
                        this.loadingBooking = false
                        this.$emit('onErrorHandler', message || 'Get Reservation failed')
                    }
                })
                .catch(error => {
                    if (!silent) this.loadingBooking = false
                    if (error.response && error.response.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else if (!silent) {
                        this.$emit('onErrorHandler', error.message)
                    }
                });
        },
        onClickChangeState(state) {
            this.state = state
            this.$emit('onClickChangeState', state)
        },
        onError(msg) {
            this.$emit('onErrorHandler', msg)
        },
        onShowInfoDialog(msg) {
            this.infoMsg = msg
            this.infoDialog = true
        },
        SQLDate(date) {
            return moment(date).format('YYYY-MM-DD')
        },
        format_date(value) {
            if (value) {
                return moment(value).format('DD/MM/YYYY')
            }
        },
        // แสดง createdate/editdate (TIMESTAMP จาก backend) เป็นเวลาไทยคงที่
        // ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้
        format_datetime(value) {
            return formatDateTimeBKK(value, 'DD/MM/YYYY HH:mm')
        },
        // double-click a row → show booking audit info (who booked / when, and who
        // edited / when). updateby/editdate only render when the booking was edited.
        // ดึงไทม์ไลน์ก่อนเปิด — เปิดก่อนแล้วค่อยเติมทำให้บล็อกประวัติกระพริบ
        async showBookingInfo(item) {
            this.bookingInfoItem = Object.assign({}, item)
            this.$emit('onLoading', true)
            try {
                this.historyPreload = await fetchEditHistory(
                    this.baseURL, this.$store.getters.getToken, 'booking', item.reservationid)
            } finally {
                this.$emit('onLoading', false)
            }
            this.dialogBookingInfo = true
        },
        async showAddNewBooking() {
            this.$emit('onLoading', true)
            await this.getCourseLookup()
            await this.getFamilyLookup()
            await this.getStudentLookup()
            this.editedBookingIndex = -1;
            this.editedBookingItem = Object.assign({}, this.defaultBookingItem);
            this.$emit('onLoading', false)
            this.dialogBookingEdit = true;
        },
        // ── เรียนรายครั้ง (walk-in) ──
        async togglePerSession() {
            this.perSessionMode = !this.perSessionMode;
            if (this.perSessionMode) {
                if (!this.psPaydate) this.psPaydate = this.SQLDate(new Date());
                await this.fetchCoursePrices();
                this.autoFillPsPrice();
            }
        },
        togglePsPaid() {
            this.psPaid = this.psPaid == 1 ? 0 : 1;
            if (this.psPaid != 1) {
                this.psSlipFiles = [];
                this.psPaydate = null;
            } else if (!this.psPaydate) {
                this.psPaydate = this.SQLDate(new Date());
            }
        },
        // จำนวนเงิน 2 ตำแหน่งเสมอ — สูตรกลางอยู่ที่ utils/money.js
        formatMoney,
        applyPsAutoFee() {
            // .toFixed(2) เหมือนอีก 2 หน้าที่มีช่องนี้ — เดิมขาดไปช่องเดียว ค่าธรรมเนียม 207.00 จึงโชว์ '207'
            this.psFeeValue = normalizeMoneyInput(this.psComputedFee);
        },
        // แก้ราคา → ค่าธรรมเนียมตามไปด้วย เว้นแต่แอดมินขอแก้เอง (พฤติกรรมเดียวกับหน้าคอร์ส)
        onPsPriceChanged() {
            if (!this.psFeeManual) this.applyPsAutoFee();
        },
        // เปลี่ยนวิธีจ่าย = เปลี่ยนฐานของค่าธรรมเนียมทั้งก้อน → ล้าง override เสมอ
        onPsMethodChanged() {
            this.psFeeManual = false;
            this.applyPsAutoFee();
        },
        confirmPsFeeEdit() {
            this.psFeeManual = true;
            this.showPsFeeEditConfirm = false;
        },
        resetPsFeeToAuto() {
            this.psFeeManual = false;
            this.applyPsAutoFee();
        },
        resetPsFields() {
            this.perSessionMode = false;
            this.psPrice = null;
            this.psPaymentMethod = '';
            this.psPaid = 1;
            this.psPaydate = null;
            this.psSlipFiles = [];
            this.psShortnote = '';
            this.psFeeValue = 0;
            this.psFeeManual = false;
            this.showPsFeeEditConfirm = false;
        },
        // ราคากลาง — ดึงครั้งเดียวต่อการเปิดหน้า ใช้เติมราคา "ซื้อ 1 ครั้ง" อัตโนมัติ
        async fetchCoursePrices() {
            if (this.coursePrices.length > 0) return;
            try {
                const token = this.$store.getters.getToken;
                const response = await axios.get(this.baseURL + '/getCoursePrices', { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success) {
                    this.coursePrices = response.data.results || [];
                    if (Number.isFinite(Number(response.data.creditCardFeeRate))) {
                        this.creditCardFeeRate = Number(response.data.creditCardFeeRate);
                    }
                }
            } catch (e) { /* ราคากลางเป็นแค่ตัวช่วย prefill — ดึงไม่ได้ก็ยังกรอกราคาเองได้ */ }
        },
        // เติมราคาอัตโนมัติจากราคากลางแถว quantity=1 ของคอร์สที่เลือก (แอดมินพิมพ์ทับได้)
        autoFillPsPrice() {
            if (!this.perSessionMode || !this.editedBookingItem.courseid) return;
            const found = this.coursePrices.find(p => p.courseid === this.editedBookingItem.courseid
                && p.coursetype === 'Limited' && String(p.quantity) === '1');
            if (found) {
                this.psPrice = found.price;
                // ราคาถูกเซ็ตด้วยโค้ด ไม่ใช่การพิมพ์ → @input ไม่ยิง ต้องอัปเดตค่าธรรมเนียมเอง
                if (!this.psFeeManual) this.applyPsAutoFee();
            }
        },
        async doSavePerSessionBooking() {
            // multipart เพราะมีสลิป — ฝั่ง server ระวังสตริง 'null' ให้แล้ว แต่ส่งค่าว่างแทน null เสมอ
            const formData = new FormData();
            formData.append('studentid', this.editedBookingItem.studentid);
            formData.append('classid', this.editedBookingItem.classtime.classid);
            formData.append('classdate', this.SQLDate(this.selectedDate));
            formData.append('classtime', this.editedBookingItem.classtime.classtime);
            formData.append('classday', this.editedBookingItem.classtime.classday);
            formData.append('blindflag', this.editedBookingItem.blindflag ? 1 : 0);
            formData.append('price', this.psPrice);
            formData.append('paymentmethod', this.psPaymentMethod || '');
            formData.append('payment_fee', this.psFeeValue);
            formData.append('paid', this.psPaid);
            formData.append('paydate', this.psPaid == 1 ? (this.psPaydate || '') : '');
            formData.append('shortnote', this.psShortnote || '');
            this.psSlipFiles.forEach((f) => formData.append('slipImage', f));
            const token = this.$store.getters.getToken;
            await axios.post(this.baseURL + '/addPerSessionBookingByAdmin', formData, { headers: { Authorization: `Bearer ${token}` } })
                .then(response => {
                    if (response.data.success) {
                        this.$emit('onInfoHandler', response.data.message || this.$t('msg.bookingAdded'));
                        this.getReservationList()
                        this.dialogBookingEdit = false
                    } else {
                        this.$emit('onErrorHandler', response.data.message || this.$t('msg.bookingAddFail'));
                    }
                    this.$emit('onUpdateDataSuccess')
                })
                .catch(error => {
                    if (error.response?.status == 401) {
                        this.$emit('onErrorHandler', error.response.data.message)
                        this.$emit('onClickChangeState', 'login')
                    } else {
                        this.$emit('onErrorHandler', error.response?.data?.message || error.message)
                    }
                });
        },
    },
    watch: {
        dialogBookingEdit(val) {
            val || this.closeEditBooking()
        },
        dialogCheckin(val) {
            val || this.clickCancelCheckinDialog()
        },
        dialogUndoCheckin(val) {
            val || this.clickCancelUndoCheckinDialog()
        },
        dialogBookingDelete(val) {
            val || this.clickCancelDeleteBooking()
        },
        // ปิด confirm ชั้นที่สอง (ยืนยันหรือถอย) → ล้างใบจองที่เลือกไว้แบบเดียวกับ dialog อื่น
        dialogPsPaidCancel(val) {
            if (!val) {
                setTimeout(() => {
                    this.$nextTick(() => {
                        this.editedBookingItem = Object.assign({}, this.defaultBookingItem)
                        this.editedBookingIndex = -1
                    })
                }, 300)
            }
        },
        // เปลี่ยนคอร์สใน dialog จอง → เติมราคารายครั้งจากราคากลางของคอร์สใหม่
        'editedBookingItem.courseid'() {
            this.autoFillPsPrice()
        },

    },
    computed: {
        ...mapGetters({
            token: 'getToken',
        }),
        tomorrow() {
            const d = new Date()
            d.setDate(d.getDate() + 1)
            return d
        },
        today() {
            return new Date()
        },
        formBookingTitle() {
            return this.editedBookingIndex === -1 ? this.$t('btn.newBooking') : this.$t('btn.edit')
        },
        // ── เรียนรายครั้ง ──
        psPaymentOptions() {
            return [
                { value: 'cash', label: this.$t('customerCourse.paymentCash') },
                { value: 'transfer', label: this.$t('customerCourse.paymentTransfer') },
                { value: 'credit', label: this.$t('customerCourse.paymentCredit') },
            ];
        },
        // ต้องให้ผลตรงกับ computePaymentFee() ฝั่ง server ทั้งสูตรและการปัดเศษ (แนวเดียวกับ CustomerCourse.vue)
        psComputedFee() {
            if (this.psPaymentMethod !== 'credit') return 0;
            const price = Number(this.psPrice);
            if (!Number.isFinite(price) || price <= 0) return 0;
            return Math.round(price * this.creditCardFeeRate * 100) / 100;
        },
        // 0.03 → "3" (ตัดศูนย์ท้ายทิ้ง เช่น 0.0325 → "3.25") — สูตรเดียวกับ CustomerCourse.vue
        psFeeRatePercentLabel() {
            return String(Number((this.creditCardFeeRate * 100).toFixed(4)));
        },
        hasMainCourse() {
            return !!this.editedBookingItem.courseinfo && this.courseinfoColor === 'courseinfoColorGreen';
        },
        // ล็อกปุ่มบันทึกเมื่อข้อมูลรายครั้งไม่ครบ (ราคาบังคับ + วิธีจ่ายบังคับเมื่อติ๊กจ่ายแล้ว)
        psInvalid() {
            if (!this.perSessionMode || this.editedBookingIndex !== -1 || this.editedBookingItem.freeflag == 1) return false;
            const price = Number(this.psPrice);
            if (!Number.isFinite(price) || price <= 0) return true;
            // ติ๊ก "จ่ายแล้ว" = ต้องรู้ครบว่า จ่ายเมื่อไหร่ / ด้วยวิธีไหน / ค่าธรรมเนียมเท่าไหร่
            // (ราคาเช็คไปแล้วข้างบน) — เช็ค "ว่างหรือไม่" ไม่ใช่ความจริง/เท็จ เพราะค่าธรรมเนียม
            // 0.00 เป็นค่าที่ถูกต้องและพบบ่อยที่สุด
            const blank = (v) => v === null || v === undefined || String(v).trim() === '';
            if (this.psPaid == 1 && (blank(this.psPaydate) || !this.psPaymentMethod || blank(this.psFeeValue))) return true;
            return false;
        },
        // ── period view (ดูตามช่วงเวลา) — mirrors PrivateBookingManagement.vue ──
        viewModes() {
            return [
                { key: 'day', label: this.$t('privateBooking.periodDay') },
                { key: 'month', label: this.$t('privateBooking.periodMonth') },
            ]
        },
        // the span fetched in month mode = the whole month containing `date`
        monthStart() { return moment(this.date).startOf('month').toDate() },
        monthEnd() { return moment(this.date).endOf('month').toDate() },
        // what IdCalendar shows selected/anchored (non-padded 'YYYY-M-D' like its keyOf)
        calendarModel() {
            return this.date ? (this.date.getFullYear() + '-' + (this.date.getMonth() + 1) + '-' + this.date.getDate()) : ''
        },
        // booking days among the LOADED rows, keyed 'YYYY-M-D' to match IdCalendar's dot keys.
        // Day mode only ever loads ONE day, so a dot there would just be a lone unexplained mark under
        // the day already highlighted as selected — dots (and their legend) are month-mode only.
        markKeys() {
            if (this.viewMode !== 'month') return []
            return [...new Set(this.BookingList.map(b => moment(b.classdatekey || b.classdate).format('YYYY-M-D')))]
        },
        // datebar label — day: the selected date (DD/MM/YYYY, matches the grid); month: Thai Buddhist month name
        rangeLabel() {
            if (this.viewMode === 'month') return formatMonthYear(this.date)
            return this.format_date(this.date)
        },
        // กติกายกเลิก (เจ้าของเคาะ 2026-08-10): ผู้จัดการยกเลิกได้ทุกใบ — บทบาทอื่นติดกรอบ 3 วัน
        // backend บังคับจริงอีกชั้นที่ /cancelBookingByAdmin เสมอ ตรงนี้แค่ไม่โชว์ปุ่มที่กดแล้วโดนปฏิเสธ
        isHead() {
            const u = this.$store.getters.getUserdata;
            return String(u && u.usertype) === '0';
        },
        // quick totals for the month overview chip (month mode)
        summary() {
            const rows = this.BookingList
            const students = new Set(rows.map(r => r.studentid).filter(Boolean))
            const days = new Set(rows.map(r => r.classdatekey || this.SQLDate(r.classdate)))
            return { count: rows.length, students: students.size, days: days.size }
        },
        BookingListHeaders() {
            return [
                { title: this.$t('table.name'), key: 'fullname' },
                { title: this.$t('table.courseName'), key: 'coursename' },
                // shown in both modes (like the Private page) so the column layout never shifts when
                // switching mode; it is the only way to tell rows apart in month mode
                { title: this.$t('table.date'), key: 'classdatedisplay' },
                { title: this.$t('table.time'), key: 'classtime' },
                { title: this.$t('table.view'), key: 'view', align: 'center', sortable: false },
                { title: this.$t('table.checkin'), key: 'checkin', align: 'center', sortable: false },
                { title: this.$t('table.edit'), key: 'edit', align: 'center', sortable: false },
                { title: this.$t('table.delete'), key: 'delete', align: 'center', sortable: false },
            ]
        },
        // IdDataGrid renders rows in the given order. The backend already returns
        // classdate ASC → classtime ASC → reservationid DESC (newest booking first within a slot);
        // re-sorting here on the same keys keeps that order explicit and survives a stale/older backend.
        // Array.prototype.sort is stable, so equal (date, time) rows keep the backend's reservationid DESC.
        // NOTE: sort REFERENCES, never clones — the row handed to the row actions is looked up with
        // BookingList.indexOf(item), which silently returns -1 (= "add new") against a copied object.
        sortedBookingList() {
            return [...this.BookingList].sort((a, b) => {
                const d = String(a.classdatekey || '').localeCompare(String(b.classdatekey || ''))
                return d !== 0 ? d : String(a.classtime).localeCompare(String(b.classtime))
            })
        },
        // toolbar filters (client-side, IdDataGrid): by course + by check-in status
        bookingFilters() {
            const courses = [...new Set(this.BookingList.map(b => b.coursename).filter(Boolean))].map(c => ({ value: c, label: c }))
            return [
                { key: 'coursename', label: this.$t('table.courseName'), options: courses },
                { key: 'checkedin', label: this.$t('table.checkin'), options: [
                    { value: 1, label: this.$t('bookingList.menuCheckedInBadge') },
                    { value: 0, label: this.$t('bookingList.notCheckedIn') },
                ] },
            ]
        },
    }

})
import { Promise } from 'core-js';
const DashboardAPI = {
    baseURL: env.SERVER_URL,
    // startdate/enddate are inclusive and equal in day mode. `classdate` is sent too so the request still
    // works against an older backend that only knows the single-day contract (it ignores the range keys).
    fetchDataBooking({ token, startdate, enddate }) {
        return new Promise(resolve => {
            //console.log('DashboardAPI : ' + this.baseURL+'/getReservationList')
            axios
                .post(this.baseURL + '/getReservationList', {
                    classdate: startdate, startdate, enddate
                },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    })
                .then(response => {
                    //console.log('fetchDataBooking result',response);
                    if (response.data.success) {
                        const datalist = response.data.results
                        resolve({ success: true, results: datalist })
                    } else {
                        resolve({ success: true, results: [] })
                    }
                })
                .catch(error => {
                    resolve({ success: false, error: error })
                });
        });
    },
}
</script>
<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: lightgray;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background-color: lightgray;
}

.scrollable-content {
  max-height: 950px;
  overflow-y: auto;
}

/* .info-rows/.info-row/.info-label/.info-val + .badge-private/.badge-public moved to the
   global istar-pages.css — now shared with PrivateBookingManagement.vue's audit-info dialog. */

.courseinfoColorRed {
    color: red;
}

.courseinfoColorGreen {
    color: green;
}

.font-card {
    font-size: 20px;
    font-weight: bold;
}

button span.mdi,
.dashboard-card span.mdi {
    font-size: 22px;
    margin-right: 5px;
}

.dashboard-card:hover {
    background-color: rgba(187, 187, 187, 0.342);
    cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.pulse {
    cursor: pointer;
    /*box-shadow: 0 0 0 rgba(204,169,44, 0.4);*/
    box-shadow: 0px 2px 1px -1px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.12));
    animation: pulse 2s infinite;
}

.pulse:hover {
    animation: none;
}

@-webkit-keyframes pulse {
    0% {
        -webkit-box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(255, 0, 0, 0.2)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.12));
    }

    70% {
        -webkit-box-shadow: 0 0 0 10px var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
    }

    100% {
        -webkit-box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
    }
}

@keyframes pulse {
    0% {
        -moz-box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(255, 0, 0, 0.4)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.12));
        box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0.4)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(255, 0, 0, 0.12));
    }

    70% {
        -moz-box-shadow: 0 0 0 10px var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
        box-shadow: 0 0 0 10px var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
    }

    100% {
        -moz-box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
        box-shadow: 0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(204, 169, 44, 0)), 0px 1px 1px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.14)), 0px 1px 3px 0px var(--v-shadow-key-penumbra-opacity, rgba(204, 169, 44, 0.12));
    }
}
</style>